// RRuleSchedulingSection.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
  RadioGroup,
  Radio,
  Tooltip,
  IconButton,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  CalendarToday as CalendarIcon,
  Loop,
  ChevronLeft,
  ChevronRight,
  Today as TodayIcon,
} from '@mui/icons-material';
import { RRule } from 'rrule';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../../../i18n/LocalizedDateDistance';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  isSameDay,
  isSameMonth,
  format,
  differenceInCalendarDays,
  differenceInCalendarMonths,
} from 'date-fns';
import { FormSection } from '../../../../components/common/form';
import { nonWorkingDaysAPI } from '../../../settings/services/nonWorkingDays';

// Calendar-style rows of 7 (weekdays / days of month) with flexbox: each child
// takes a seventh of the row minus its share of the six gaps. A child with
// data-span="n" covers n columns (and the n - 1 gaps between them).
const sevenColumns = (gap) => {
  const column = (theme) => `((100% - 6 * ${theme.spacing(gap)}) / 7)`;
  return {
    display: 'flex',
    flexWrap: 'wrap',
    gap,
    '& > *': { flex: (theme) => `0 0 calc(${column(theme)})`, minWidth: 0 },
    '& > [data-span="4"]': { flex: (theme) => `0 0 calc(4 * ${column(theme)} + 3 * ${theme.spacing(gap)})` },
  };
};

// Mini calendar for the recurrence preview. Occurrences are filled circles;
// each occurrence's deadline window (occurrence → occurrence + the task's
// duration) is a tinted band behind the days, like a date-range picker.
const DAY_SIZE = 30;

const MiniCalendar = ({ month, occurrences, skipped = [], closingDays = new Map(), durationDays }) => {
  const { t, i18n } = useTranslation(['common', 'tasks']);
  const locale = getDateLocale(i18n.language);

  const calendarStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
  const days = [];
  for (let d = calendarStart; d <= calendarEnd; d = addDays(d, 1)) days.push(d);

  const isOccurrence = (date) => occurrences.some((o) => isSameDay(o, date));
  const isSkipped = (date) => skipped.some((o) => isSameDay(o, date));
  const closingName = (date) => closingDays.get(format(date, 'yyyy-MM-dd'));
  // Inside some occurrence's deadline window (after the occurrence day itself)
  const inWindow = (date) => durationDays > 0 && occurrences.some((o) => {
    const diff = differenceInCalendarDays(date, o);
    return diff > 0 && diff <= durationDays;
  });

  const weekDayLabels = Array.from({ length: 7 }, (_, i) =>
    format(addDays(calendarStart, i), 'EEEEEE', { locale }));

  return (
    <Box sx={{ flex: '1 1 220px', maxWidth: 300 }}>
      <Typography variant="subtitle2" fontWeight={700} textAlign="center" mb={2} sx={{ textTransform: 'capitalize' }}>
        {format(month, 'LLLL yyyy', { locale })}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', rowGap: 1 }}>
        {weekDayLabels.map((label, idx) => (
          <Typography key={idx} variant="caption" color="text.secondary" fontWeight={600} textAlign="center" sx={{ pb: 1 }}>
            {label}
          </Typography>
        ))}
        {days.map((d) => {
          if (!isSameMonth(d, month)) return <Box key={d.toISOString()} />;

          const occurrence = isOccurrence(d);
          const band = inWindow(d);
          // Band continues from the occurrence / previous day and into the next
          // day, except across week (row) and month edges
          const weekday = (d.getDay() + 6) % 7;
          const prev = addDays(d, -1);
          const next = addDays(d, 1);
          const joinsLeft = band && weekday > 0 && isSameMonth(prev, month) && (inWindow(prev) || isOccurrence(prev));
          const joinsRight = (band || occurrence) && weekday < 6 && isSameMonth(next, month) && inWindow(next);
          const isToday = isSameDay(d, new Date());
          const closed = closingName(d);
          const skippedHere = isSkipped(d);

          const tip = [
            occurrence && t('tasks:form.legend.scheduled_date'),
            skippedHere && t('tasks:form.legend.skipped'),
            band && t('tasks:form.legend.duration_period'),
            closed,
          ].filter(Boolean).join(' · ');

          return (
            <Box
              key={d.toISOString()}
              sx={(theme) => {
                const tint = theme.palette.primary.lighter;
                const half = (side) => `linear-gradient(to ${side}, ${tint} 50%, transparent 50%)`;
                return {
                  display: 'flex',
                  justifyContent: 'center',
                  // Full band on middle days, half band on the ends
                  background: band
                    ? (joinsLeft && joinsRight ? tint : joinsLeft ? half('right') : joinsRight ? half('left') : 'transparent')
                    : (joinsRight ? half('left') : 'transparent'),
                };
              }}
            >
              <Tooltip title={tip} disableHoverListener={!tip}>
                <Box
                  sx={{
                    width: DAY_SIZE,
                    height: DAY_SIZE,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: isToday || occurrence ? 700 : 400,
                    color: occurrence ? 'primary.contrastText' : isToday ? 'primary.main' : closed ? 'text.disabled' : 'text.primary',
                    bgcolor: occurrence ? 'primary.main' : band ? 'primary.lighter' : closed ? 'action.hover' : 'transparent',
                    // Occurrence dropped because it falls on a closing day
                    ...(skippedHere && { border: '2px dashed', borderColor: 'primary.main' }),
                  }}
                >
                  {format(d, 'd')}
                </Box>
              </Tooltip>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const CalendarLegend = ({ showSkipped }) => {
  const { t } = useTranslation(['tasks']);
  const dot = (sx) => <Box sx={{ width: 14, height: 14, borderRadius: '50%', flexShrink: 0, ...sx }} />;
  const items = [
    [dot({ bgcolor: 'primary.main' }), t('tasks:form.legend.scheduled_date')],
    [<Box sx={{ width: 24, height: 12, borderRadius: 6, bgcolor: 'primary.lighter', flexShrink: 0 }} />, t('tasks:form.legend.duration_period')],
    [dot({ bgcolor: 'action.hover' }), t('tasks:form.legend.closing_day')],
    showSkipped && [dot({ border: '2px dashed', borderColor: 'primary.main' }), t('tasks:form.legend.skipped')],
  ].filter(Boolean);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mt: 3 }}>
      {items.map(([mark, label]) => (
        <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {mark}
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const RRuleSchedulingSection = ({ formData, onInputChange, validationErrors = {}, loading = false, number }) => {
  const { t, i18n } = useTranslation(['common', 'tasks']);

  // Local state for RRule configuration
  const [frequency, setFrequency] = useState(RRule.WEEKLY);
  const [interval, setInterval] = useState(1);
  const [count, setCount] = useState('');
  const [until, setUntil] = useState('');
  const [selectedWeekdays, setSelectedWeekdays] = useState([]);
  const [monthDay, setMonthDay] = useState(1);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [daysToComplete, setDaysToComplete] = useState('');
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [endType, setEndType] = useState('never'); // 'never', 'count', 'until'
  const [monthlyType, setMonthlyType] = useState('dayOfMonth'); // 'dayOfMonth' or 'nthWeekday'
  const [yearlyType, setYearlyType] = useState('dayOfMonth'); // 'dayOfMonth' or 'nthWeekday'
  const [nthWeekday, setNthWeekday] = useState(1); // 1=first, 2=second, 3=third, 4=fourth, -1=last
  const [selectedWeekday, setSelectedWeekday] = useState(RRule.MO); // The weekday for nth weekday option
  const [yearlyMonth, setYearlyMonth] = useState(1); // Month for yearly recurrence (1-12)
  const [calendarOffset, setCalendarOffset] = useState(0); // Offset for calendar navigation (0 = current month)

  // Initialize daysToComplete from existing due_date (edit mode)
  useEffect(() => {
    if (formData.scheduled_date && formData.due_date && !initialDataLoaded) {
      const scheduledDate = formData.scheduled_date instanceof Date
        ? formData.scheduled_date
        : new Date(formData.scheduled_date);
      const dueDate = formData.due_date instanceof Date
        ? formData.due_date
        : new Date(formData.due_date);

      const diffTime = dueDate.getTime() - scheduledDate.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays >= 0) {
        setDaysToComplete(diffDays.toString());
      }

      setInitialDataLoaded(true);
    }
  }, [formData.scheduled_date, formData.due_date, initialDataLoaded]);

  // Parse existing rrule when formData changes (edit mode)
  useEffect(() => {
    if (formData.rrule && !isInitialized) {
      try {
        const rule = RRule.fromString(formData.rrule);
        const options = rule.origOptions;

        // Set frequency
        if (options.freq !== undefined) {
          setFrequency(options.freq);
        }

        // Set interval
        if (options.interval !== undefined) {
          setInterval(options.interval);
        }

        // Set count
        if (options.count !== undefined) {
          setCount(options.count.toString());
          setEndType('count');
        }

        // Set until
        if (options.until) {
          const untilDate = new Date(options.until);
          setUntil(untilDate.toISOString().split('T')[0]);
          setEndType('until');
        }

        // Set weekdays for weekly recurrence
        if (options.byweekday && Array.isArray(options.byweekday)) {
          setSelectedWeekdays(options.byweekday);
        }

        // Set month day for monthly recurrence
        if (options.bymonthday !== undefined) {
          const dayValue = Array.isArray(options.bymonthday)
            ? options.bymonthday[0]
            : options.bymonthday;
          setMonthDay(dayValue);
          setMonthlyType('dayOfMonth');
        }

        // Set nth weekday for monthly/yearly recurrence (e.g., "first Monday")
        if (options.bysetpos !== undefined && options.byweekday) {
          const posValue = Array.isArray(options.bysetpos)
            ? options.bysetpos[0]
            : options.bysetpos;
          setNthWeekday(posValue);

          const weekdayValue = Array.isArray(options.byweekday)
            ? options.byweekday[0]
            : options.byweekday;
          setSelectedWeekday(weekdayValue);

          if (options.freq === RRule.YEARLY) {
            setYearlyType('nthWeekday');
          } else {
            setMonthlyType('nthWeekday');
          }
        }

        // Set months
        if (options.bymonth && Array.isArray(options.bymonth)) {
          setSelectedMonths(options.bymonth);
        }

        // Set yearly month (bymonth is an array; take the first element)
        if (options.freq === RRule.YEARLY && options.bymonth) {
          const monthValue = Array.isArray(options.bymonth)
            ? options.bymonth[0]
            : options.bymonth;
          setYearlyMonth(monthValue);
        }

        setIsInitialized(true);
      } catch (e) {
        console.error('Failed to parse rrule:', e);
      }
    }
  }, [formData.rrule, isInitialized]);

  // Reset initialization flag when rrule is cleared
  useEffect(() => {
    if (!formData.rrule) {
      setIsInitialized(false);
    }
  }, [formData.rrule]);

  // Calculate due_date when scheduled_date or daysToComplete changes
  useEffect(() => {
    if (formData.scheduled_date && daysToComplete && initialDataLoaded) {
      const scheduledDate = formData.scheduled_date instanceof Date
        ? formData.scheduled_date
        : new Date(formData.scheduled_date);
      const dueDate = new Date(scheduledDate);
      dueDate.setDate(dueDate.getDate() + parseInt(daysToComplete));
      onInputChange('due_date', dueDate);
    } else if (!daysToComplete && initialDataLoaded) {
      onInputChange('due_date', null);
    }
  }, [formData.scheduled_date, daysToComplete, initialDataLoaded, onInputChange]);

  // Deadline presets map onto daysToComplete; "custom" is sticky so typing
  // 7 in the custom field doesn't flip the selection to "1 uge"
  const [customDeadline, setCustomDeadline] = useState(false);
  const startForDeadline = formData.scheduled_date ? new Date(formData.scheduled_date) : new Date();
  const monthDays = differenceInCalendarDays(addMonths(startForDeadline, 1), startForDeadline);
  const presetDays = { same_day: 0, week: 7, month: monthDays };
  const matchingPreset = Object.keys(presetDays).find((key) => String(presetDays[key]) === String(daysToComplete));
  const deadlinePreset = customDeadline ? 'custom'
    : daysToComplete === '' ? null
      : (matchingPreset ?? 'custom');
  const handleDeadlinePreset = (preset) => {
    setCustomDeadline(preset === 'custom');
    if (preset !== 'custom') setDaysToComplete(String(presetDays[preset]));
  };
  const dueDateObj = formData.due_date ? new Date(formData.due_date) : null;

  const weekdayOptions = [
    { value: RRule.MO, label: t('tasks:recurrence_values.weekdays.mon') },
    { value: RRule.TU, label: t('tasks:recurrence_values.weekdays.tue') },
    { value: RRule.WE, label: t('tasks:recurrence_values.weekdays.wed') },
    { value: RRule.TH, label: t('tasks:recurrence_values.weekdays.thu') },
    { value: RRule.FR, label: t('tasks:recurrence_values.weekdays.fri') },
    { value: RRule.SA, label: t('tasks:recurrence_values.weekdays.sat') },
    { value: RRule.SU, label: t('tasks:recurrence_values.weekdays.sun') },
  ];

  const monthOptions = [
    { value: 1, label: t('tasks:recurrence_values.months.jan') },
    { value: 2, label: t('tasks:recurrence_values.months.feb') },
    { value: 3, label: t('tasks:recurrence_values.months.mar') },
    { value: 4, label: t('tasks:recurrence_values.months.apr') },
    { value: 5, label: t('tasks:recurrence_values.months.may') },
    { value: 6, label: t('tasks:recurrence_values.months.jun') },
    { value: 7, label: t('tasks:recurrence_values.months.jul') },
    { value: 8, label: t('tasks:recurrence_values.months.aug') },
    { value: 9, label: t('tasks:recurrence_values.months.sep') },
    { value: 10, label: t('tasks:recurrence_values.months.oct') },
    { value: 11, label: t('tasks:recurrence_values.months.nov') },
    { value: 12, label: t('tasks:recurrence_values.months.dec') },
  ];

  const frequencyOptions = [
    { value: RRule.DAILY, label: t('tasks:recurrence_values.frequencies.daily') },
    { value: RRule.WEEKLY, label: t('tasks:recurrence_values.frequencies.weekly') },
    { value: RRule.MONTHLY, label: t('tasks:recurrence_values.frequencies.monthly') },
    { value: RRule.YEARLY, label: t('tasks:recurrence_values.frequencies.yearly') },
  ];

  const toggleWeekday = (day) => {
    setSelectedWeekdays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const toggleMonth = (month) => {
    setSelectedMonths(prev =>
      prev.includes(month)
        ? prev.filter(m => m !== month)
        : [...prev, month]
    );
  };

  // Build RRule object
  const rruleObject = useMemo(() => {
    if (!formData.scheduled_date) return null;

    const options = {
      freq: frequency,
      interval: parseInt(interval) || 1,
      dtstart: new Date(formData.scheduled_date),
    };

    if (endType === 'count' && count) {
      options.count = parseInt(count);
    } else if (endType === 'until' && until) {
      options.until = new Date(until);
    }

    if (frequency === RRule.WEEKLY) {
      options.byweekday = selectedWeekdays.length > 0
        ? selectedWeekdays
        : [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA, RRule.SU];
    }

    if (frequency === RRule.MONTHLY) {
      if (monthlyType === 'dayOfMonth') {
        options.bymonthday = parseInt(monthDay) || 1;
      } else {
        // nth weekday (e.g., first Monday, last Friday)
        options.byweekday = selectedWeekday;
        options.bysetpos = nthWeekday;
      }
      if (selectedMonths.length > 0) {
        options.bymonth = selectedMonths;
      }
    }

    if (frequency === RRule.YEARLY) {
      options.bymonth = yearlyMonth;
      if (yearlyType === 'dayOfMonth') {
        options.bymonthday = parseInt(monthDay) || 1;
      } else {
        // nth weekday (e.g., first Monday of March, last Friday of December)
        options.byweekday = selectedWeekday;
        options.bysetpos = nthWeekday;
      }
    }

    try {
      return new RRule(options);
    } catch (e) {
      console.error('Failed to create rrule:', e);
      return null;
    }
  }, [formData.scheduled_date, frequency, interval, count, until, selectedWeekdays, monthDay, selectedMonths, endType, monthlyType, yearlyType, nthWeekday, selectedWeekday, yearlyMonth]);

  // Preview: three months starting at the task's start month (not today)
  const scheduledDate = formData.scheduled_date ? new Date(formData.scheduled_date) : null;
  const baseMonth = startOfMonth(scheduledDate && !isNaN(scheduledDate) ? scheduledDate : new Date());
  const previewMonths = [0, 1, 2].map((i) => addMonths(baseMonth, calendarOffset + i));
  // Each occurrence is due the same number of days after it as the task itself
  const durationDays = scheduledDate && formData.due_date
    ? Math.max(0, differenceInCalendarDays(new Date(formData.due_date), scheduledDate))
    : 0;
  const previewFrom = addDays(previewMonths[0], -durationDays); // windows spilling into the first month
  const previewTo = endOfMonth(previewMonths[2]);
  const previewKey = `${format(previewFrom, 'yyyy-MM-dd')}_${format(previewTo, 'yyyy-MM-dd')}`;

  // Department closing days + public holidays in the preview range; only
  // full-day ones block scheduling (same rule as the backend)
  const [closingDays, setClosingDays] = useState(() => new Map());
  useEffect(() => {
    let cancelled = false;
    const [start, end] = previewKey.split('_');
    nonWorkingDaysAPI.getExpanded(start, end)
      .then((res) => {
        if (cancelled) return;
        const entries = res.data?.non_working_days ?? res.data?.data?.non_working_days ?? [];
        setClosingDays(new Map(entries.filter((e) => e.is_full_day).map((e) => [e.date, e.name])));
      })
      .catch(() => { if (!cancelled) setClosingDays(new Map()); });
    return () => { cancelled = true; };
  }, [previewKey]);

  // The backend starts at the rule's first occurrence on/after the start date,
  // and (unless closing days are allowed) drops occurrences on closing days in
  // favour of the rule's next one.
  const { previewOccurrences, skippedOccurrences } = useMemo(() => {
    if (!rruleObject) return { previewOccurrences: [], skippedOccurrences: [] };
    const all = rruleObject.between(previewFrom, previewTo, true);
    if (formData.skip_closing_days) return { previewOccurrences: all, skippedOccurrences: [] };
    const blocked = (d) => closingDays.has(format(d, 'yyyy-MM-dd'));
    return {
      previewOccurrences: all.filter((d) => !blocked(d)),
      skippedOccurrences: all.filter(blocked),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rruleObject, previewKey, closingDays, formData.skip_closing_days]);

  // Push the rule string to the form after render — doing it inside the memo
  // updated the parent's state while rendering this component (React warning).
  const rruleString = rruleObject ? rruleObject.toString() : null;
  useEffect(() => {
    if (rruleString) onInputChange('rrule', rruleString);
  }, [rruleString, onInputChange]);

  const nextOccurrences = useMemo(() => {
    if (!rruleObject || !formData.is_recurring) return [];
    return rruleObject.all((date, i) => i < 6);
  }, [rruleObject, formData.is_recurring]);

  const humanReadable = useMemo(() => {
    if (!rruleObject) return '';
    return rruleObject.toText();
  }, [rruleObject]);

  return (
    <FormSection
      number={number}
      title={t('tasks:form.sections.scheduling')}
    >

        <Box display="flex" flexDirection="column" gap={3}>
          {/* Start date */}
          <DatePicker
            label={t('tasks:form_labels.scheduling.start_date')}
            value={formData.scheduled_date || null}
            onChange={(newValue) => onInputChange('scheduled_date', newValue)}
            disabled={loading}
            format="dd/MM/yyyy"
            minDate={new Date()}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!validationErrors.scheduled_date,
                helperText: validationErrors.scheduled_date,
                InputProps: {
                  startAdornment: <CalendarIcon sx={{ mr: 1, color: 'action.active' }} />,
                }
              }
            }}
          />

          {/* Deadline: common presets, or a custom number of days */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} mb={2}>
              {t('tasks:form.deadline.label')}
            </Typography>
            <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
              <ToggleButtonGroup
                exclusive
                size="small"
                color="primary"
                value={deadlinePreset}
                onChange={(_, value) => value && handleDeadlinePreset(value)}
                disabled={loading}
              >
                {['same_day', 'week', 'month', 'custom'].map((preset) => (
                  <ToggleButton key={preset} value={preset} sx={{ px: 4, textTransform: 'none' }}>
                    {t(`tasks:form.deadline.${preset}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {deadlinePreset === 'custom' && (
                <TextField
                  size="small"
                  type="number"
                  value={daysToComplete}
                  onChange={(e) => setDaysToComplete(e.target.value)}
                  disabled={loading}
                  autoFocus={customDeadline}
                  inputProps={{ min: 0, 'aria-label': t('tasks:form.deadline.days') }}
                  InputProps={{ endAdornment: <InputAdornment position="end">{t('tasks:form.deadline.days')}</InputAdornment> }}
                  sx={{ width: 140 }}
                />
              )}
            </Box>
            <Typography variant="body2" color={validationErrors.due_date ? 'error' : 'text.secondary'} mt={2}>
              {validationErrors.due_date
                || (dueDateObj && !isNaN(dueDateObj)
                  ? t('tasks:form.deadline.due_on', { date: format(dueDateObj, 'EEEE d. MMMM yyyy', { locale: getDateLocale(i18n.language) }) })
                  : t('tasks:form.deadline.pick'))}
            </Typography>
          </Box>

          <Divider />

          {/* Recurring Toggle */}
          <Box display="flex" alignItems="center" gap={2}>
            <Loop color="secondary" />
            <Box flex={1}>
              <Typography variant="h6" fontWeight="medium">
                {t('tasks:form.scheduling.recurring_task')}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_recurring || false}
                  onChange={(e) => {
                    onInputChange('is_recurring', e.target.checked);
                    // Clear rrule when turning off recurring
                    if (!e.target.checked) {
                      onInputChange('rrule', null);
                    }
                  }}
                  color="secondary"
                  disabled={loading}
                />
              }
              label={!formData.is_recurring ? t('tasks:form.scheduling.adhoc_task') : t('tasks:form.scheduling.recurring_task')}
              labelPlacement='start'
            />
          </Box>

          {formData.is_recurring && (
            <Box display="flex" flexDirection="column" gap={3}>
              {/* Frequency and Interval */}
              <Box display="flex" gap={2} flexWrap="wrap">
                <FormControl sx={{ minWidth: 200, flex: 2 }}>
                  <InputLabel id="r-rule-scheduling-section-frequency-label">{t('tasks:form.scheduling.frequency')}</InputLabel>
                  <Select labelId="r-rule-scheduling-section-frequency-label"
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    label={t('tasks:form.scheduling.frequency')}
                    disabled={loading}
                  >
                    {frequencyOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label={
                    frequency === RRule.DAILY ? t('tasks:form_labels.scheduling.interval_days_label') :
                    frequency === RRule.WEEKLY ? t('tasks:form_labels.scheduling.interval_weeks_label') :
                    frequency === RRule.MONTHLY ? t('tasks:form_labels.scheduling.interval_months_label') :
                    t('tasks:form_labels.scheduling.interval_years_label')
                  }
                  type="number"
                  value={interval}
                  onChange={(e) => setInterval(e.target.value)}
                  inputProps={{ min: 1 }}
                  helperText={
                    frequency === RRule.DAILY ? t('tasks:form_labels.scheduling.interval_days', { count: parseInt(interval) || 1 }) :
                    frequency === RRule.WEEKLY ? t('tasks:form_labels.scheduling.interval_weeks', { count: parseInt(interval) || 1 }) :
                    frequency === RRule.MONTHLY ? t('tasks:form_labels.scheduling.interval_months', { count: parseInt(interval) || 1 }) :
                    t('tasks:form_labels.scheduling.interval_years', { count: parseInt(interval) || 1 })
                  }
                  disabled={loading}
                  sx={{ minWidth: 200, flex: 1 }}
                />
              </Box>

              {/* Weekly: Weekday Selection */}
              {frequency === RRule.WEEKLY && (
                <Box>
                  <Typography variant="body2" gutterBottom fontWeight="medium">
                    {t('tasks:form.scheduling.repeat_on')}
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {weekdayOptions.map((day) => (
                      <Chip
                        key={day.value}
                        label={day.label}
                        onClick={() => !loading && toggleWeekday(day.value)}
                        color={selectedWeekdays.includes(day.value) ? 'primary' : 'default'}
                        variant={selectedWeekdays.includes(day.value) ? 'filled' : 'outlined'}
                        disabled={loading}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Monthly: Day of Month or Nth Weekday */}
              {frequency === RRule.MONTHLY && (
                <>
                  <Box>
                    <Typography variant="body2" gutterBottom fontWeight="medium">
                      {t('tasks:form.scheduling.repeat_by')}
                    </Typography>
                    <RadioGroup
                      value={monthlyType}
                      onChange={(e) => setMonthlyType(e.target.value)}
                      row
                    >
                      <FormControlLabel
                        value="dayOfMonth"
                        control={<Radio disabled={loading} />}
                        label={t('tasks:form.scheduling.day_of_month')}
                      />
                      <FormControlLabel
                        value="nthWeekday"
                        control={<Radio disabled={loading} />}
                        label={t('tasks:form.scheduling.nth_weekday')}
                      />
                    </RadioGroup>
                  </Box>

                  {monthlyType === 'dayOfMonth' ? (
                    <Box>
                      <Box
                        sx={{ ...sevenColumns(0.5), maxWidth: 350 }}
                      >
                        {[...Array(31)].map((_, idx) => {
                          const day = idx + 1;
                          const isSelected = parseInt(monthDay) === day;
                          return (
                            <Box
                              key={day}
                              onClick={() => !loading && setMonthDay(day)}
                              sx={{
                                textAlign: 'center',
                                py: 1,
                                px: 0.5,
                                cursor: loading ? 'default' : 'pointer',
                                borderRadius: 1,
                                fontSize: '0.875rem',
                                fontWeight: isSelected ? 'bold' : 'normal',
                                backgroundColor: isSelected ? 'primary.main' : 'transparent',
                                color: isSelected ? 'primary.contrastText' : 'text.primary',
                                border: '1px solid',
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                opacity: loading ? 0.5 : 1,
                                '&:hover': !loading && !isSelected ? {
                                  backgroundColor: 'action.hover',
                                  borderColor: 'primary.light',
                                } : {},
                              }}
                            >
                              {day}
                            </Box>
                          );
                        })}
                        {/* Last day option */}
                        <Box
                          data-span="4"
                          onClick={() => !loading && setMonthDay(-1)}
                          sx={{
                            textAlign: 'center',
                            py: 1,
                            px: 0.5,
                            cursor: loading ? 'default' : 'pointer',
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: parseInt(monthDay) === -1 ? 'bold' : 'normal',
                            backgroundColor: parseInt(monthDay) === -1 ? 'primary.main' : 'transparent',
                            color: parseInt(monthDay) === -1 ? 'primary.contrastText' : 'text.primary',
                            border: '1px solid',
                            borderColor: parseInt(monthDay) === -1 ? 'primary.main' : 'divider',
                            opacity: loading ? 0.5 : 1,
                            '&:hover': !loading && parseInt(monthDay) !== -1 ? {
                              backgroundColor: 'action.hover',
                              borderColor: 'primary.light',
                            } : {},
                          }}
                        >
                          {t('tasks:form.scheduling.nth_options.last_day')}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                      <FormControl sx={{ minWidth: 150 }}>
                        <InputLabel id="r-rule-scheduling-section-occurrence-label">{t('tasks:form.scheduling.occurrence')}</InputLabel>
                        <Select labelId="r-rule-scheduling-section-occurrence-label"
                          value={nthWeekday}
                          onChange={(e) => setNthWeekday(e.target.value)}
                          label={t('tasks:form.scheduling.occurrence')}
                          disabled={loading}
                        >
                          <MenuItem value={1}>{t('tasks:form.scheduling.nth_options.first')}</MenuItem>
                          <MenuItem value={2}>{t('tasks:form.scheduling.nth_options.second')}</MenuItem>
                          <MenuItem value={3}>{t('tasks:form.scheduling.nth_options.third')}</MenuItem>
                          <MenuItem value={4}>{t('tasks:form.scheduling.nth_options.fourth')}</MenuItem>
                          <MenuItem value={-1}>{t('tasks:form.scheduling.nth_options.last')}</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl sx={{ minWidth: 150 }}>
                        <InputLabel id="r-rule-scheduling-section-weekday-label">{t('tasks:form.scheduling.weekday')}</InputLabel>
                        <Select labelId="r-rule-scheduling-section-weekday-label"
                          value={selectedWeekday}
                          onChange={(e) => setSelectedWeekday(e.target.value)}
                          label={t('tasks:form.scheduling.weekday')}
                          disabled={loading}
                        >
                          {weekdayOptions.map((day) => (
                            <MenuItem key={day.value} value={day.value}>
                              {day.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="body2" gutterBottom fontWeight="medium">
                      {t('tasks:form.scheduling.in_months')}
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {monthOptions.map((month) => (
                        <Chip
                          key={month.value}
                          label={month.label}
                          onClick={() => !loading && toggleMonth(month.value)}
                          color={selectedMonths.includes(month.value) ? 'primary' : 'default'}
                          variant={selectedMonths.includes(month.value) ? 'filled' : 'outlined'}
                          disabled={loading}
                        />
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {selectedMonths.length === 0
                        ? t('tasks:form.scheduling.all_months_selected')
                        : t('tasks:form.scheduling.months_selected', { count: selectedMonths.length })}
                    </Typography>
                  </Box>
                </>
              )}

              {/* Yearly: Day of Month or Nth Weekday */}
              {frequency === RRule.YEARLY && (
                <>
                  <Box>
                    <Typography variant="body2" gutterBottom fontWeight="medium">
                      {t('tasks:form.scheduling.repeat_by')}
                    </Typography>
                    <RadioGroup
                      value={yearlyType}
                      onChange={(e) => setYearlyType(e.target.value)}
                      row
                    >
                      <FormControlLabel
                        value="dayOfMonth"
                        control={<Radio disabled={loading} />}
                        label={t('tasks:form.scheduling.day_of_month')}
                      />
                      <FormControlLabel
                        value="nthWeekday"
                        control={<Radio disabled={loading} />}
                        label={t('tasks:form.scheduling.nth_weekday')}
                      />
                    </RadioGroup>
                  </Box>

                  {yearlyType === 'dayOfMonth' ? (
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box>
                        <Typography variant="body2" gutterBottom fontWeight="medium">
                          {t('tasks:form.scheduling.day_of_month')}
                        </Typography>
                        <Box
                          sx={{ ...sevenColumns(0.5), maxWidth: 350 }}
                        >
                          {[...Array(31)].map((_, idx) => {
                            const day = idx + 1;
                            const isSelected = parseInt(monthDay) === day;
                            return (
                              <Box
                                key={day}
                                onClick={() => !loading && setMonthDay(day)}
                                sx={{
                                  textAlign: 'center',
                                  py: 1,
                                  px: 0.5,
                                  cursor: loading ? 'default' : 'pointer',
                                  borderRadius: 1,
                                  fontSize: '0.875rem',
                                  fontWeight: isSelected ? 'bold' : 'normal',
                                  backgroundColor: isSelected ? 'primary.main' : 'transparent',
                                  color: isSelected ? 'primary.contrastText' : 'text.primary',
                                  border: '1px solid',
                                  borderColor: isSelected ? 'primary.main' : 'divider',
                                  opacity: loading ? 0.5 : 1,
                                  '&:hover': !loading && !isSelected ? {
                                    backgroundColor: 'action.hover',
                                    borderColor: 'primary.light',
                                  } : {},
                                }}
                              >
                                {day}
                              </Box>
                            );
                          })}
                          {/* Last day option */}
                          <Box
                            data-span="4"
                          onClick={() => !loading && setMonthDay(-1)}
                            sx={{
                              textAlign: 'center',
                              py: 1,
                              px: 0.5,
                              cursor: loading ? 'default' : 'pointer',
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: parseInt(monthDay) === -1 ? 'bold' : 'normal',
                              backgroundColor: parseInt(monthDay) === -1 ? 'primary.main' : 'transparent',
                              color: parseInt(monthDay) === -1 ? 'primary.contrastText' : 'text.primary',
                              border: '1px solid',
                              borderColor: parseInt(monthDay) === -1 ? 'primary.main' : 'divider',
                              opacity: loading ? 0.5 : 1,
                                '&:hover': !loading && parseInt(monthDay) !== -1 ? {
                                backgroundColor: 'action.hover',
                                borderColor: 'primary.light',
                              } : {},
                            }}
                          >
                            {t('tasks:form.scheduling.nth_options.last_day')}
                          </Box>
                        </Box>
                      </Box>

                      <Box>
                        <Typography variant="body2" gutterBottom fontWeight="medium">
                          {t('tasks:form.scheduling.in_month')}
                        </Typography>
                        <FormControl fullWidth sx={{ maxWidth: 200 }}>
                          <InputLabel id="r-rule-scheduling-section-in-month-label">{t('tasks:form.scheduling.in_month')}</InputLabel>
                          <Select labelId="r-rule-scheduling-section-in-month-label"
                            value={yearlyMonth}
                            onChange={(e) => setYearlyMonth(e.target.value)}
                            label={t('tasks:form.scheduling.in_month')}
                            disabled={loading}
                          >
                            {monthOptions.map((month) => (
                              <MenuItem key={month.value} value={month.value}>
                                {month.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  ) : (
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                        <FormControl sx={{ minWidth: 150 }}>
                          <InputLabel id="r-rule-scheduling-section-occurrence-2-label">{t('tasks:form.scheduling.occurrence')}</InputLabel>
                          <Select labelId="r-rule-scheduling-section-occurrence-2-label"
                            value={nthWeekday}
                            onChange={(e) => setNthWeekday(e.target.value)}
                            label={t('tasks:form.scheduling.occurrence')}
                            disabled={loading}
                          >
                            <MenuItem value={1}>{t('tasks:form.scheduling.nth_options.first')}</MenuItem>
                            <MenuItem value={2}>{t('tasks:form.scheduling.nth_options.second')}</MenuItem>
                            <MenuItem value={3}>{t('tasks:form.scheduling.nth_options.third')}</MenuItem>
                            <MenuItem value={4}>{t('tasks:form.scheduling.nth_options.fourth')}</MenuItem>
                            <MenuItem value={-1}>{t('tasks:form.scheduling.nth_options.last')}</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 150 }}>
                          <InputLabel id="r-rule-scheduling-section-weekday-2-label">{t('tasks:form.scheduling.weekday')}</InputLabel>
                          <Select labelId="r-rule-scheduling-section-weekday-2-label"
                            value={selectedWeekday}
                            onChange={(e) => setSelectedWeekday(e.target.value)}
                            label={t('tasks:form.scheduling.weekday')}
                            disabled={loading}
                          >
                            {weekdayOptions.map((day) => (
                              <MenuItem key={day.value} value={day.value}>
                                {day.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>

                      <Box>
                        <Typography variant="body2" gutterBottom fontWeight="medium">
                          {t('tasks:form.scheduling.in_month')}
                        </Typography>
                        <FormControl fullWidth sx={{ maxWidth: 200 }}>
                          <InputLabel id="r-rule-scheduling-section-in-month-2-label">{t('tasks:form.scheduling.in_month')}</InputLabel>
                          <Select labelId="r-rule-scheduling-section-in-month-2-label"
                            value={yearlyMonth}
                            onChange={(e) => setYearlyMonth(e.target.value)}
                            label={t('tasks:form.scheduling.in_month')}
                            disabled={loading}
                          >
                            {monthOptions.map((month) => (
                              <MenuItem key={month.value} value={month.value}>
                                {month.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {/* End Conditions */}
              <Box>
                <Typography variant="body2" gutterBottom fontWeight="medium">
                  {t('tasks:form.scheduling.end_condition')}
                </Typography>
                <RadioGroup
                  value={endType}
                  onChange={(e) => setEndType(e.target.value)}
                  row
                >
                  <FormControlLabel
                    value="never"
                    control={<Radio disabled={loading} />}
                    label={t('tasks:form.scheduling.no_end_date')}
                  />
                  <FormControlLabel
                    value="count"
                    control={<Radio disabled={loading} />}
                    label={t('tasks:form.scheduling.after_occurrences')}
                  />
                  <FormControlLabel
                    value="until"
                    control={<Radio disabled={loading} />}
                    label={t('tasks:form.scheduling.end_by_date')}
                  />
                </RadioGroup>

                {endType === 'count' && (
                  <Box display="flex" alignItems="center" gap={2} mt={2}>
                    <TextField
                      type="number"
                      value={count}
                      onChange={(e) => setCount(e.target.value)}
                      inputProps={{ min: 1 }}
                      disabled={loading}
                      size="small"
                      sx={{ width: 100 }}
                    />
                    <Typography>
                      {t('tasks:form.scheduling.occurrences_label')}
                    </Typography>
                  </Box>
                )}

                {endType === 'until' && (
                  <Box mt={2}>
                    <>
                      <DatePicker
                        value={until ? new Date(until) : null}
                        onChange={(newValue) => {
                          if (newValue) {
                            setUntil(newValue.toISOString().split('T')[0]);
                          } else {
                            setUntil('');
                          }
                        }}
                        minDate={formData.scheduled_date || new Date()}
                        disabled={loading}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { width: 180 },
                          }
                        }}
                      />
                    </>
                  </Box>
                )}
              </Box>

              {/* 3-Month Calendar Preview */}
              {rruleObject && (
                <Box sx={{ py: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="body2" fontWeight="medium">
                      {t('tasks:form.scheduling.schedule')}
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <IconButton
                        size="small"
                        onClick={() => setCalendarOffset(prev => prev - 1)}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <ChevronLeft fontSize="small" />
                      </IconButton>
                      <Button
                        size="small"
                        startIcon={<TodayIcon />}
                        onClick={() => setCalendarOffset(differenceInCalendarMonths(new Date(), baseMonth))}
                        variant="outlined"
                        sx={{ minWidth: 100 }}
                      >
                        {t('common:dateHints.today')}
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => setCalendarOffset(prev => prev + 1)}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <ChevronRight fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'space-around',
                      gap: 6,
                      p: 4,
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {previewMonths.map((monthDate) => (
                      <MiniCalendar
                        key={monthDate.toISOString()}
                        month={monthDate}
                        occurrences={previewOccurrences}
                        skipped={skippedOccurrences}
                        closingDays={closingDays}
                        durationDays={durationDays}
                      />
                    ))}
                  </Box>
                  <CalendarLegend showSkipped={!formData.skip_closing_days} />
                </Box>
              )}

          {/* Skip Closing Days Toggle */}
          {formData.is_recurring && (
            <Box display="flex" alignItems="center" gap={2}>
              <Box flex={1}>
                <Typography variant="body1" fontWeight="medium">
                  {t('tasks:form.scheduling.skip_closing_days')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('tasks:form.scheduling.skip_closing_days_help')}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.skip_closing_days || false}
                    onChange={(e) => onInputChange('skip_closing_days', e.target.checked)}
                    color="primary"
                    disabled={loading}
                  />
                }
                label={formData.skip_closing_days ? t('tasks:form.scheduling.allow_closing_days') : t('tasks:form.scheduling.respect_closing_days')}
                labelPlacement='start'
              />
            </Box>
          )}

            </Box>
          )}
        </Box>
        {/* Calendar View */}
        {/* {rruleObject && formData.is_recurring && formData.scheduled_date && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Calendar View
            </Typography>
            <CalendarView 
              rrule={rruleObject} 
              startDate={formData.scheduled_date}
              endDate={until}
            />
          </Box>
        )}

        {/* RRule String Output */}
        {/* {rruleObject && formData.is_recurring && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              RRule String (stored in database):
            </Typography>
            <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
              {rruleObject.toString()}
            </Typography>
          </Box>
        )} */}
    </FormSection>
  );
};

export default RRuleSchedulingSection;